import { IonButton, IonCard, IonCol, IonIcon, IonInput, IonItem, IonLabel, IonRow, isPlatform } from "@ionic/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { OrganizationMembershipType, Membership, OrganizationAgreements } from "../../../models";
import { Input } from "reactstrap";
import { isWindows } from "../../../utilities/platform/Platform";
import Spinner from "../../Spinners/Spinner";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";

interface _Props {
    membershipType: OrganizationMembershipType | undefined;
    membership: Membership | undefined;
};

interface FormattedRuleSignature {
    firstName: string
    lastName: string
    signedOn: string
}

interface FormattedRuleInterface {
    ruleTitle: string
    ruleBody: string
    ruleSignatures: FormattedRuleSignature[]
}

const EditMembershipApplicationWaivers: React.FC<_Props> = ({membershipType, membership}) => {

    const [rules, setRules] = useState<FormattedRuleInterface[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [signedOnDate, setSignedOnDate] = useState("");

    const getDataOnLoad = async () => {
        setIsLoading(true);
        const orgRules = membershipType?.organizationRules || [];
        const formattedRules: FormattedRuleInterface[] = [];
        for (let i = 0; i < orgRules.length; i++) {
            const currentOrgRule = orgRules[i];
            const agreements: ((OrganizationAgreements | null)[] | undefined) = membership?.organizationAgreements?.filter((item) => {
                return currentOrgRule?.id === item?.organizationRuleId;
            });
            const formattedAgreements: FormattedRuleSignature[] = [];
            if (agreements) {
                for (let j = 0; j < agreements.length; j++) {
                    const currentAgreement = agreements[j];
                    const formatted: FormattedRuleSignature = {
                        firstName: currentAgreement?.firstName || "",
                        lastName: currentAgreement?.lastName || "",
                        signedOn: currentAgreement?.createdOn || ""
                    };
                    formattedAgreements.push(formatted);
                }
            }
            const formattedRule: FormattedRuleInterface = {
                ruleTitle: currentOrgRule?.title || "No title",
                ruleBody: currentOrgRule?.body || "No text",
                ruleSignatures: formattedAgreements
            };
            formattedRules.push(formattedRule);
        }
        setRules(formattedRules);
        setIsLoading(false);
    }

    useEffect(() => {
        getDataOnLoad();
    }, []);

    const onClickSaveSignature = () => {
        setIsLoading(true);

        // Save in DB

        // Add the new signature to the Rules state

        // Clear out the form

        setIsLoading(false);
    }

    return (
        <React.Fragment>
            <IonCard color="white" className="ion-padding">
                <h3>Waivers</h3>

                {isLoading ?
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <Spinner />
                        </IonCol>
                    </IonRow>
                    :
                    <>
                        {(rules && rules.length > 0) ? 
                            <>
                                {rules.map((rule, i) => (
                                    <div key={i}>
                                        <IonRow className="ion-justify-content-center">
                                            <h6>
                                                {rule?.ruleTitle}
                                            </h6>
                                        </IonRow>
                                        <IonRow>
                                            <Input
                                                id="waiver-list-body-info"
                                                rows={(isWindows() || isPlatform("desktop")) ? "5" : "15"}
                                                type="textarea"
                                                name="waiver-sign-description"
                                                value={rule?.ruleBody}
                                                className="mt-3"
                                                disabled={true}
                                            />
                                        </IonRow>
                                        <IonRow>
                                            <IonCol>
                                                {(rule.ruleSignatures && rule.ruleSignatures.length > 0) ?
                                                    <>
                                                        {rule.ruleSignatures.map((signature, k) => (
                                                            <IonRow key={k}>
                                                                <IonCol>
                                                                    <p>
                                                                        Signed by: {signature.firstName} {signature.lastName}
                                                                    </p>
                                                                    <p>
                                                                        Signed on: {signature.signedOn}
                                                                    </p>
                                                                </IonCol>
                                                            </IonRow>
                                                        ))}
                                                    </>
                                                    :
                                                    <>
                                                        <p>No signatures yet.</p>
                                                    </>
                                                }
                                            </IonCol>
                                        </IonRow>
                                        <hr/>
                                        <IonRow className="ion-justify-content-center">
                                            <IonCol size="12" className="ion-text-center">
                                                <h6>
                                                    Add a New Signature
                                                </h6>
                                            </IonCol>
                                        </IonRow>
                                        <IonRow>
                                            <IonCol sizeXs="12" sizeMd="4">
                                                <IonItem color="white">
                                                    <IonLabel position="floating">
                                                        First Name <RequiredInputIndicator />
                                                    </IonLabel>
                                                    <IonInput 
                                                        type="text"
                                                        value={firstName}
                                                        aria-required={true}
                                                        onIonChange={e => {
                                                            setFirstName(e.detail.value!);
                                                        }}
                                                    />
                                                </IonItem>
                                            </IonCol>
                                            <IonCol sizeXs="12" sizeMd="4">
                                                <IonItem color="white">
                                                    <IonLabel position="floating">
                                                        Last Name <RequiredInputIndicator />
                                                    </IonLabel>
                                                    <IonInput 
                                                        type="text"
                                                        value={lastName}
                                                        aria-required={true}
                                                        onIonChange={e => {
                                                            setLastName(e.detail.value!);
                                                        }}
                                                    />
                                                </IonItem>
                                            </IonCol>
                                            <IonCol sizeXs="12" sizeMd="4">
                                                <IonItem color="white">
                                                    <IonLabel position="stacked">
                                                        Date Signed <RequiredInputIndicator />
                                                    </IonLabel>
                                                    <IonInput 
                                                        type="date"
                                                        value={signedOnDate}
                                                        aria-required={true}
                                                        onIonChange={e => {
                                                            setSignedOnDate(e.detail.value!)
                                                        }}
                                                    />
                                                </IonItem>
                                            </IonCol>
                                        </IonRow>
                                        <IonRow className="ion-justify-content-center">
                                            <IonCol size="12" className="ion-text-center">
                                                <IonButton
                                                    color="tertiary"
                                                    onClick={onClickSaveSignature}
                                                >
                                                    Save New Signature
                                                </IonButton>
                                            </IonCol>
                                        </IonRow>
                                    </div>
                                ))}
                            </>
                            :
                            <p>This membership type does not have any waivers to sign.</p>
                        }
                    </>
                }

                {/* {membershipType?.organizationRules && membershipType?.organizationRules?.length === 0 ?
                    <p>This membership type does not have any waivers to sign.</p>
                :
                    (membershipType?.organizationRules?.map((rule, index) => {
                        const found = membership?.organizationAgreements?.find((item) => {
                            return rule?.id === item?.organizationRuleId;
                        });
                        return(found ?
                            <div key={index}>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        {found?.organizationRuleTitle}
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        Signed by: {found?.firstName} {found?.lastName}
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol/>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        Signed on: {moment(found?.createdOn).format("MM/DD/YYYY")}
                                    </IonCol>
                                </IonRow>
                                <hr/>
                            </div>
                        :
                            <div key={index}>
                                <IonRow className="ion-justify-content-center">
                                    <h6>
                                        {rule?.title}
                                    </h6>
                                </IonRow>
                                <IonRow>
                                    <Input
                                        id="waiver-list-body-info"
                                        rows={(isWindows() || isPlatform("desktop")) ? "5" : "15"}
                                        type="textarea"
                                        name="waiver-sign-description"
                                        value={rule?.body}
                                        className="mt-3"
                                        disabled={true}
                                    />
                                </IonRow>
                                <IonRow>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        {rule?.title}
                                    </IonCol>
                                    <IonCol sizeXs="12" sizeMd="6">
                                        No signature found.
                                    </IonCol>
                                </IonRow>
                                <hr/>
                            </div>
                        )
                    }))
                } */}
            </IonCard>
        </React.Fragment>
    );
};

export default EditMembershipApplicationWaivers;